import { Component } from '@theme/component';
import { clamp } from '@theme/utilities';

/** Pointer gestures shared by mouse, pen and touch, only inside product zoom. */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller;
  #scale = 1;
  #x = 0;
  #y = 0;
  #pointers = new Map();
  #gesture;
  #lastTap;
  #observer = new ResizeObserver(() => this.#render());

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('pointerdown', this.#down, { signal });
    this.addEventListener('pointermove', this.#move, { signal });
    this.addEventListener('pointerup', this.#up, { signal });
    this.addEventListener('pointercancel', this.#cancel, { signal });
    this.addEventListener('lostpointercapture', this.#cancel, { signal });
    this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.addEventListener('load', () => this.#render(), { signal, capture: true });
    this.#observer.observe(this);
    this.resetZoom();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer.disconnect();
    this.resetZoom();
    super.disconnectedCallback();
  }

  get zoomLevel() { return this.#scale; }
  get maxZoom() {
    const image = this.refs.image;
    if (!image?.naturalWidth || !this.clientWidth || !this.clientHeight) return 2;
    const fit = Math.min(this.clientWidth / image.naturalWidth, this.clientHeight / image.naturalHeight);
    return Math.max(1, Math.min(4, 1 / fit));
  }
  zoomBy(amount) { this.#setZoom(this.#scale + amount); }
  resetZoom() {
    this.#pointers.clear();
    this.#gesture = undefined;
    this.#lastTap = undefined;
    this.#scale = 1; this.#x = 0; this.#y = 0;
    this.#render();
  }

  #setZoom(value, point) {
    const next = clamp(value, 1, this.maxZoom);
    const rect = this.getBoundingClientRect();
    const x = point ? point.x - rect.left - rect.width / 2 : 0;
    const y = point ? point.y - rect.top - rect.height / 2 : 0;
    const ratio = next / this.#scale;
    this.#x = x - (x - this.#x) * ratio;
    this.#y = y - (y - this.#y) * ratio;
    this.#scale = next;
    this.#render();
  }

  #down = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.setPointerCapture(event.pointerId);
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.#pointers.size === 1) {
      this.#gesture = { x: event.clientX, y: event.clientY, tx: this.#x, ty: this.#y, scale: this.#scale, moved: false, pinched: false };
    } else if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      this.#gesture = { ...this.#gesture, distance: Math.hypot(a.x-b.x,a.y-b.y), scale: this.#scale, pinched: true, moved: true };
      this.#lastTap = undefined;
    }
    this.dataset.dragging = 'true';
  };

  #move = (event) => {
    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const gesture = this.#gesture;
    if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      if (gesture.distance > 0) this.#setZoom(gesture.scale * Math.hypot(a.x-b.x,a.y-b.y) / gesture.distance, {x:(a.x+b.x)/2,y:(a.y+b.y)/2});
      return;
    }
    if (gesture.pinched) return;
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
    if (Math.hypot(dx, dy) > 8) gesture.moved = true;
    if (gesture.scale > 1 && gesture.moved) {
      this.#x = gesture.tx + dx; this.#y = gesture.ty + dy; this.#render();
    }
  };

  #up = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    const gesture = this.#gesture;
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size) return;
    this.#gesture = undefined;
    delete this.dataset.dragging;
    if (!gesture || gesture.pinched) return;
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
    if (gesture.moved) {
      this.#lastTap = undefined;
      if (gesture.scale === 1 && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        this.dispatchEvent(new CustomEvent('zoom-swipe', { bubbles: true, detail: { direction: dx < 0 ? 1 : -1 } }));
      }
      return;
    }
    const point = { x: event.clientX, y: event.clientY };
    if (event.pointerType === 'mouse') {
      this.#setZoom(this.#scale > 1 ? 1 : 2, point);
    } else {
      const now = performance.now();
      if (this.#lastTap && now - this.#lastTap.time < 300 && Math.hypot(point.x - this.#lastTap.x, point.y - this.#lastTap.y) < 35) {
        this.#setZoom(this.#scale > 1 ? 1 : 2, point); this.#lastTap = undefined;
      } else this.#lastTap = { ...point, time: now };
    }
  };

  #cancel = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.clear(); this.#gesture = undefined; this.#lastTap = undefined;
    delete this.dataset.dragging;
  };

  #render() {
    const image = this.refs.image;
    if (!image) return;
    this.#scale = clamp(this.#scale, 1, this.maxZoom);
    const fit = image.naturalWidth ? Math.min(this.clientWidth/image.naturalWidth,this.clientHeight/image.naturalHeight) : 1;
    const limitX = Math.max(0, (image.naturalWidth * fit * this.#scale - this.clientWidth) / 2);
    const limitY = Math.max(0, (image.naturalHeight * fit * this.#scale - this.clientHeight) / 2);
    this.#x = clamp(this.#x, -limitX, limitX); this.#y = clamp(this.#y, -limitY, limitY);
    if (this.#scale === 1) { this.#x = 0; this.#y = 0; }
    this.style.setProperty('--drag-zoom-scale', String(this.#scale));
    this.style.setProperty('--drag-zoom-translate-x', `${this.#x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#y}px`);
    this.dataset.zoomed = String(this.#scale > 1);
    this.dispatchEvent(new CustomEvent('zoom-change', { bubbles: true }));
  }
}

if (!customElements.get('drag-zoom-wrapper')) customElements.define('drag-zoom-wrapper', DragZoomWrapper);
